import React from 'react';
import { useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import AttachIcon from 'assets/chat/ActionIcon/attach.svg';
import EmojiBlackIcon from 'assets/chat/ActionIcon/emojiBlack.svg';
import SendIcon from 'assets/chat/ActionIcon/send.svg';
import EmojiPicker from 'emoji-picker-react';
import {
  getMessages,
  getUnreadCount,
  markConversationAsRead,
  sendMessage,
} from 'features/chat/chatActions';
import { selectSendingMessage } from 'features/chat/chatSlice';

import {
  ActionsAttachButton,
  AttachButtonIcon,
  ChatInput,
  ChatInputContainer,
  ChatInputContainerActions,
  ChatInputContainerFlex,
  ChatSingleBlock,
  ChatSingleContent,
  EmojiButtonIcon,
  EmojiPickerWrapper,
  HiddenFileInput,
  ChatSingleItemLeft as LoadingMessageContainer,
  ChatSingleItemLeftContent as LoadingMessageContent,
  ChatSingleItemLeftMessageText as LoadingMessageText,
  SendButton,
  SendButtonIcon,
} from './ChatSingle.styles';
import ChatSingleItemLeft from './components/ChatSingleItemLeft';
import ChatSingleItemRight from './components/ChatSingleItemRight';
import FilePreviewContainerComponent from './components/FilePreviewContainer';

const ChatSingle = ({ conversation, messages = [], loading, platform, permissionType }) => {
  const dispatch = useDispatch();
  const isSending = useSelector(selectSendingMessage);
  const messagesTotal = useSelector((state) => state.chat.data.messagesTotal);
  const isViewOnly = permissionType === 'view_only';

  const [value, setValue] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const textareaRef = useRef(null);
  const contentRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const fileInputRef = useRef(null);
  const hasMarkedAsReadRef = useRef(false);
  const isLoadingMoreRef = useRef(false);
  const previousScrollHeightRef = useRef(0);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = '36px';

      const style = window.getComputedStyle(textarea);
      const padding = parseInt(style.paddingTop) + parseInt(style.paddingBottom);

      const newHeight = Math.min(textarea.scrollHeight - padding, 71);
      textarea.style.height = `${Math.max(newHeight, 36)}px`;
    }
  }, [value]);

  useEffect(() => {
    if (contentRef.current && !isLoadingMoreRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    } else if (contentRef.current && isLoadingMoreRef.current) {
      const currentScrollHeight = contentRef.current.scrollHeight;
      const scrollHeightDifference = currentScrollHeight - previousScrollHeightRef.current;
      contentRef.current.scrollTop = scrollHeightDifference;
      isLoadingMoreRef.current = false;
    }
  }, [messages]);

  useEffect(() => {
    hasMarkedAsReadRef.current = false;
    setSelectedFile(null);
    setFilePreviewUrl(null);
    setHasMore(true);
    setLoadingMore(false);
    isLoadingMoreRef.current = false;
  }, [conversation?.conversationId]);

  useEffect(() => {
    if (messagesTotal > 0) {
      setHasMore(messages.length < messagesTotal);
    }
  }, [messages.length, messagesTotal]);

  useEffect(() => {
    return () => {
      if (filePreviewUrl) {
        URL.revokeObjectURL(filePreviewUrl);
      }
    };
  }, [filePreviewUrl]);

  useEffect(() => {
    const handleScroll = () => {
      const element = contentRef.current;
      if (!element || !conversation?.conversationId) return;

      const isAtTop = element.scrollTop < 50;

      if (isAtTop && hasMore && !loadingMore && !loading) {
        setLoadingMore(true);
        isLoadingMoreRef.current = true;
        previousScrollHeightRef.current = element.scrollHeight;

        const LIMIT = 10;
        const newOffset = messages.length;

        dispatch(
          getMessages({
            conversationId: conversation.conversationId,
            limit: LIMIT,
            offset: newOffset,
          })
        )
          .unwrap()
          .then(() => {
            setLoadingMore(false);
          })
          .catch((error) => {
            console.error('Failed to load more messages:', error);
            setLoadingMore(false);
            isLoadingMoreRef.current = false;
          });
      }
    };

    const element = contentRef.current;
    if (element) {
      element.addEventListener('scroll', handleScroll);
      return () => element.removeEventListener('scroll', handleScroll);
    }
  }, [conversation?.conversationId, hasMore, loadingMore, loading, messages.length, dispatch]);

  useEffect(() => {
    const checkAndMarkAsRead = () => {
      const element = contentRef.current;
      if (!element || !conversation?.conversationId || hasMarkedAsReadRef.current) return;

      const isAtBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 50;

      if (isAtBottom && messages.length > 0) {
        hasMarkedAsReadRef.current = true;
        dispatch(markConversationAsRead({ conversationId: conversation.conversationId }));
        dispatch(getUnreadCount());
      }
    };

    checkAndMarkAsRead();

    const element = contentRef.current;
    if (element) {
      element.addEventListener('scroll', checkAndMarkAsRead);
      return () => element.removeEventListener('scroll', checkAndMarkAsRead);
    }
  }, [messages, conversation?.conversationId, dispatch]);

  const getFileType = (file) => {
    const mimeType = file.type;

    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'audio';
    return 'file';
  };

  const handleSendMessage = async () => {
    if (isViewOnly) return;
    if (
      (!value.trim() && !selectedFile) ||
      isSending ||
      !conversation?.conversationId ||
      !platform
    )
      return;

    const isEmailPlatform = platform === 'gmail' || platform === 'imap';

    const senderId =
      isEmailPlatform && conversation?.sender?.id ? conversation.sender.id : null;

    const messageData = {
      conversationId: conversation.conversationId,
      senderId,
      platform,
      messageText: value.trim() || undefined,
    };

    // Add file if selected
    if (selectedFile) {
      messageData.file = selectedFile;
      messageData.fileType = getFileType(selectedFile);
    }

    try {
      await dispatch(sendMessage(messageData)).unwrap();
      setValue('');
      setSelectedFile(null);
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleEmojiClick = (emojiData) => {
    const emoji = emojiData.emoji;
    const textarea = textareaRef.current;

    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = value.substring(0, start) + emoji + value.substring(end);

      setValue(newValue);

      // Set cursor position after emoji
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
        textarea.focus();
      }, 0);
    } else {
      setValue((prev) => prev + emoji);
    }
  };

  const toggleEmojiPicker = () => {
    if (isViewOnly) return;
    setShowEmojiPicker((prev) => !prev);
  };

  const handleAttachClick = () => {
    if (isViewOnly) return;
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e) => {
    if (isViewOnly) return;

    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);

      // Create preview URL for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreviewUrl(reader.result);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFilePreviewUrl(null);
  };

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target) &&
        !event.target.closest('.emoji-picker-button')
      ) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  if (loading) {
    return (
      <ChatSingleBlock>
        <ChatSingleContent>
          <LoadingMessageContainer>
            <LoadingMessageContent>
              <LoadingMessageText>Loading messages...</LoadingMessageText>
            </LoadingMessageContent>
          </LoadingMessageContainer>
        </ChatSingleContent>
      </ChatSingleBlock>
    );
  }

  return (
    <ChatSingleBlock>
      <ChatSingleContent ref={contentRef}>
        {loadingMore && (
          <LoadingMessageContainer>
            <LoadingMessageContent>
              <LoadingMessageText>Loading more messages...</LoadingMessageText>
            </LoadingMessageContent>
          </LoadingMessageContainer>
        )}
        {messages.length === 0 ? (
          <LoadingMessageContainer>
            <LoadingMessageContent>
              <LoadingMessageText>No messages yet</LoadingMessageText>
            </LoadingMessageContent>
          </LoadingMessageContainer>
        ) : (
          messages.map((message) => {
            const isMyMessage =
              message.direction === 'outgoing' ||
              message.sender?.id === '176902635512677' ||
              message.sender?.id === '17841405597364990';

            if (!isMyMessage) {
              return (
                <ChatSingleItemLeft
                  key={message._id}
                  message={message}
                  conversation={conversation}
                  platform={platform}
                />
              );
            }

            return (
              <ChatSingleItemRight key={message._id} message={message} platform={platform} />
            );
          })
        )}
      </ChatSingleContent>
      <FilePreviewContainerComponent
        selectedFile={selectedFile}
        filePreviewUrl={filePreviewUrl}
        onRemove={handleRemoveFile}
      />
      <ChatInputContainer>
        <HiddenFileInput
          ref={fileInputRef}
          type="file"
          onChange={handleFileSelect}
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.zip,.rar"
          disabled={isViewOnly}
        />
        <ChatInputContainerFlex>
          <ChatInputContainerActions>
            <ActionsAttachButton onClick={handleAttachClick} disabled={isViewOnly}>
              <AttachButtonIcon src={AttachIcon} />
            </ActionsAttachButton>
            <ActionsAttachButton
              className="emoji-picker-button"
              onClick={toggleEmojiPicker}
              disabled={isViewOnly}
            >
              <EmojiButtonIcon src={EmojiBlackIcon} />
            </ActionsAttachButton>
          </ChatInputContainerActions>
          <ChatInput
            ref={textareaRef}
            rows={1}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              isViewOnly
                ? 'You can only view messages (no reply permission)'
                : 'Write a message...'
            }
            disabled={isSending || isViewOnly}
          />
          <SendButton
            onClick={handleSendMessage}
            className={
              isSending || (!value.trim() && !selectedFile) || isViewOnly ? 'disabled' : ''
            }
            disabled={isViewOnly}
          >
            <SendButtonIcon src={SendIcon} />
          </SendButton>
        </ChatInputContainerFlex>
        {showEmojiPicker && !isViewOnly && (
          <EmojiPickerWrapper ref={emojiPickerRef}>
            <EmojiPicker
              onEmojiClick={handleEmojiClick}
              width={320}
              height={400}
              searchDisabled={false}
              skinTonesDisabled={false}
              previewConfig={{
                showPreview: false,
              }}
            />
          </EmojiPickerWrapper>
        )}
      </ChatInputContainer>
    </ChatSingleBlock>
  );
};

export default ChatSingle;
